import CryptoJS from 'crypto-js';
import moment from 'moment';
import { getStableHash } from '../utils/utils';
import { fetchApi } from '../utils/api';
import { authService } from '../services/auth.service';
// Tạo một hàm helper để truy cập OtpContext từ bên ngoài React Components
let globalOtpContextHandler: {
  showOtpModal: () => void;
  hideOtpModal: () => void;
  setOtpSuccessCallback: (callback: () => void) => void;
  setOtpErrorCallback: (callback: () => void) => void;
  setFromScreen: (from: string) => void;
} | null = null;

/**
 * Đăng ký handler cho OtpContext toàn cục
 * Gọi hàm này từ OtpProvider trong App.tsx
 */
export const registerOtpContextHandler = (handler: {
  showOtpModal: () => void;
  hideOtpModal: () => void;
  setOtpSuccessCallback: (callback: () => void) => void;
  setOtpErrorCallback: (callback: () => void) => void;
  setFromScreen: (from: string) => void;
}) => {
  globalOtpContextHandler = handler;
};

/**
 * Storage keys
 */
export const StorageKeys = {
  OTP_SECRETS: 'OTP_SECRETS',
  LAST_OTP_GLOBAL: 'LAST_OTP_GLOBAL',
};

/**
 * OTP secrets structure interface
 */
interface OtpSecrets {
  [username: string]: {
    ENCRYPTED_KEY: string;
    SHARED_TOKEN: string;
    LAST_OTP?: string;
  };
}

/**
 * Service for Smart OTP operations
 */
export class OtpService {
  private static normalizeUsername(username?: string | null): string {
    return (username || '').trim();
  }

  private static getUsernameCandidates(username: string): string[] {
    const u = this.normalizeUsername(username);
    if (!u) return [];
    const candidates = [u, u.toLowerCase(), u.toUpperCase()];
    return Array.from(new Set(candidates));
  }

  /**
   * Gets the startup OTP from the API
   * @returns Promise with the startup OTP data
   */
  static async getStartupOTP(): Promise<any> {
    try {
      return await fetchApi('get', 'api/SmartOTP/GetStartupOTP', {}, 'json', { skipCache: true });
    } catch (error) {
      console.error('Error getting startup OTP:', error);
      throw error;
    }
  }

  /**
   * Sends the startup OTP setup request to the server
   * @param clientToken Generated client token
   * @param otp One-time password
   * @param deviceId Device identifier
   * @param timeDev Time difference in seconds
   * @returns Promise with the response from the server
   */
  static async setStartupOTP(clientToken: string, otp: string, deviceId: string, timeDev: number): Promise<any> {
    try {
      return await fetchApi('post', 'api/SmartOTP/SetStartupOTP', {
        ClientToken: clientToken,
        OTP: otp,
        TimeDev: timeDev,
        DeviceId: deviceId,
      }, 'json', { skipCache: true });
    } catch (error) {
      console.error('Error setting startup OTP:', error);
      throw error;
    }
  }

  /**
   * Verifies a Smart OTP code
   * @param otp OTP code to verify
   * @returns Promise with the verification response
   */
  static async verifySmartOTP(otp: string): Promise<any> {
    try {
      const deviceId = authService.generateDeviceId();
      return await fetchApi('post', `api/SmartOTP/VerifySmartOTP?otp=${otp}&deviceId=${deviceId}`, {}, 'json', { skipCache: true });
    } catch (error) {
      console.error('Error verifying Smart OTP:', error);
      throw error;
    }
  }
  /**
   * Check if Smart OTP is set up for a specific username
   * @param username The username to check
   * @returns Promise resolving to true if OTP is set up for this username and device
   */
  static async isOtpSetup(username?: string): Promise<boolean> {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) return false;
    
    try {
      // First, check if we have the secrets in localStorage
      const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
      if (!secretsJson) return false;
      
      try {
        const secrets = JSON.parse(secretsJson) as OtpSecrets;
        const candidateUsernames = this.getUsernameCandidates(normalizedUsername);
        const userSecrets = candidateUsernames.map(u => secrets[u]).find(Boolean);
        
        // If we don't have secrets stored, no need to check the API
        if (!userSecrets || !userSecrets.ENCRYPTED_KEY || !userSecrets.SHARED_TOKEN) {
          return false;
        }
        
        // If we have local secrets, also verify with the API
        const deviceId = authService.generateDeviceId();
        if (!deviceId) return false;
        
        // Make API call to verify if this device is registered for SmartOTP
        console.log('Verifying SmartOTP device registration for device:', deviceId);
        const response = await fetchApi('post', `api/SmartOTP/VerifyDeviceSmartOTP?deviceId=${deviceId}`, {}, 'json', { skipCache: true });
        
        // Return true only if both local secrets exist AND API confirms device is registered
        return response && response.Status === 'Success';
      } catch (error) {
        console.error('Error parsing OTP secrets:', error);
        return false;
      }
    } catch (error) {
      console.error('Error checking OTP setup:', error);
      return false;
    }
  }
  
  /**
   * Set up OTP with the server and store the data
   * @param username The username to set up OTP for
   * @param pin User's PIN code
   * @param otp One-time password from server
   * @param deviceId Device identifier
   * @returns Result of the setup operation
   */
  static async setStartupOtp(username: string, pin: string, otp: string, deviceId: string, startupTime?: Date): Promise<boolean> {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) {
      throw new Error('Username is required to set up OTP');
    }

    // Use provided time or get server time or local time
    let time = moment();
    try {
      // Try to get server time for more accuracy
      const serverTime = await fetchApi('get', 'api/SmartOTP/GetServerTime', {}, 'json', { skipCache: true });
      if (serverTime && serverTime.ServerTime) {
        time = moment(serverTime.ServerTime);
      }
    } catch (error) {
      console.log('Error getting server time, using local time:', error);
    }
    
    try {
      const clientToken = getStableHash(`${pin}.${time.format('YYYYMMDDHHmm')}`)
      // Calculate time difference
      const timeDev = time.diff(moment(startupTime), 'second');
      
      // Send OTP setup request to server
      const response = await this.setStartupOTP(
        clientToken,
        otp,
        deviceId,
        timeDev
      );
      
      if (response && response.Status === 'Success') {
        // Store OTP data with the shared token from response
        await this.storeOtpData(normalizedUsername, pin, response.Message);
        return true;
      } else {
        throw new Error(response.Message);
      }
    } catch (error) {
      console.error('Failed to set up OTP:', error);
      throw error;
    }
  }

  /**
   * Store the OTP setup data in localStorage for a specific username
   * @param username The username to store OTP data for
   * @param pin PIN code entered by user
   * @param sharedToken Token received from server
   */
  static async storeOtpData(username: string, pin: string, sharedToken: string): Promise<void> {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) {
      throw new Error('Username is required to store OTP data');
    }

    // Use provided time or get server time or local time
    let time = moment();
    try {
      // Try to get server time for more accuracy
      const serverTime = await fetchApi('get', 'api/SmartOTP/GetServerTime', {}, 'json', { skipCache: true });
      if (serverTime && serverTime.ServerTime) {
        time = moment(serverTime.ServerTime);
      }
    } catch (error) {
      console.log('Error getting server time, using local time:', error);
    }
    
    try {
      // Generate client token using the reusable method
      const clientToken = getStableHash(`${pin}.${time.format('yyyyMMDDHHmm')}`)
      
      // Store encrypted key (hash of PIN)
      // Using a simpler hash since sha256 isn't available
      const encryptedKey = this.createSimpleHash(pin);
      
      // Encrypt and store shared token
      const tokenToEncrypt = `${sharedToken}.${clientToken}`;
      const encryptedToken = CryptoJS.TripleDES.encrypt(tokenToEncrypt, pin).toString();
      
      // Get existing secrets or create new object
      const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
      const secrets: OtpSecrets = secretsJson ? JSON.parse(secretsJson) : {};
      
      // Add or update this user's secrets
      secrets[normalizedUsername] = {
        ENCRYPTED_KEY: encryptedKey,
        SHARED_TOKEN: encryptedToken
      };
      
      // Save back to localStorage
      localStorage.setItem(StorageKeys.OTP_SECRETS, JSON.stringify(secrets));
      
      console.log(`OTP data stored successfully for user: ${normalizedUsername}`);
    } catch (error) {
      console.error('Failed to store OTP data:', error);
      throw error;
    }
  }

  /**
   * Get the decrypted OTP token for a specific user
   * @param username The username to get the token for
   * @param pin PIN code entered by user
   * @returns Decrypted token or null if not found/invalid
   */
  static async getOtpToken(username: string, pin: string): Promise<string | null> {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) return null;
    
    try {
      const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
      if (!secretsJson) return null;
      
      const secrets = JSON.parse(secretsJson) as OtpSecrets;
      const candidateUsernames = this.getUsernameCandidates(normalizedUsername);
      const userSecrets = candidateUsernames.map(u => secrets[u]).find(Boolean);
      
      if (!userSecrets || !userSecrets.SHARED_TOKEN) return null;
      
      const encryptedToken = userSecrets.SHARED_TOKEN;
      const decryptedBytes = CryptoJS.TripleDES.decrypt(encryptedToken, pin);
      const decryptedToken = decryptedBytes.toString(CryptoJS.enc.Utf8);
      
      return decryptedToken;
    } catch (error) {
      console.error(`Failed to decrypt OTP token for user ${normalizedUsername}:`, error);
      return null;
    }
  }

  /**
   * Save the last Smart OTP code used
   * @param otpCode The OTP code to save
   * @param username Optional username to associate with OTP code. If not provided, still saves to legacy storage
   */
  static saveLastSmartOtpCode(otpCode: string, username?: string): void {
    const normalizedUsername = this.normalizeUsername(username);
    try {
      localStorage.setItem(StorageKeys.LAST_OTP_GLOBAL, otpCode);
    } catch (error) {
      console.error('Error saving last OTP to LAST_OTP_GLOBAL:', error);
    }

    if (normalizedUsername) {
      try {
        const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
        const secrets: OtpSecrets = secretsJson ? JSON.parse(secretsJson) : {};
        
        const existingKey = this.getUsernameCandidates(normalizedUsername).find(u => !!secrets[u]);
        const targetKey = existingKey || normalizedUsername;

        if (!secrets[targetKey]) {
          secrets[targetKey] = {
            ENCRYPTED_KEY: '',
            SHARED_TOKEN: ''
          };
        }
        
        // Update user's last OTP code
        secrets[targetKey].LAST_OTP = otpCode;
        
        // Save updated secrets back to storage
        localStorage.setItem(StorageKeys.OTP_SECRETS, JSON.stringify(secrets));
      } catch (error) {
        console.error('Error saving last OTP to OTP_SECRETS:', error);
      }
    }
  }

  /**
   * Get the last Smart OTP code used
   * @param username Optional username to get user-specific last OTP code
   * @returns The last saved OTP code or null if not found
   */
  static getLastSmartOtpCode(username: string): string | null {
    const normalizedUsername = this.normalizeUsername(username);

    if (normalizedUsername) {
      try {
        const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
        if (secretsJson) {
          const secrets = JSON.parse(secretsJson) as OtpSecrets;

          const candidateUsernames = this.getUsernameCandidates(normalizedUsername);
          const foundOtp = candidateUsernames
            .map(u => secrets[u]?.LAST_OTP)
            .find(v => typeof v === 'string' && v.length > 0);

          if (foundOtp) return foundOtp;
        }
      } catch (error) {
        console.error('Error getting last OTP from OTP_SECRETS:', error);
      }
    }

    try {
      const globalOtp = localStorage.getItem(StorageKeys.LAST_OTP_GLOBAL);
      return globalOtp || null;
    } catch (error) {
      console.error('Error getting last OTP from LAST_OTP_GLOBAL:', error);
    }
    
    return null;
  }

  /**
   * Verify if a PIN matches the stored encrypted key for a username
   * @param username The username to verify PIN for
   * @param pin The PIN to verify
   * @returns True if PIN matches, false otherwise
   */
  static verifyPin(username: string, pin: string): boolean {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) return false;
    
    try {
      const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
      if (!secretsJson) return false;
      
      const secrets = JSON.parse(secretsJson) as OtpSecrets;
      const candidateUsernames = this.getUsernameCandidates(normalizedUsername);
      const userSecrets = candidateUsernames.map(u => secrets[u]).find(Boolean);
      
      if (!userSecrets || !userSecrets.ENCRYPTED_KEY) return false;
      
      // Compare the stored hash with a hash of the provided PIN
      const encryptedKey = userSecrets.ENCRYPTED_KEY;
      const pinHash = this.createSimpleHash(pin);
      
      return encryptedKey === pinHash;
    } catch (error) {
      console.error(`Failed to verify PIN for user ${normalizedUsername}:`, error);
      return false;
    }
  }

  /**
   * Global function for Smart OTP verification with multiple layers of protection
   * This function standardizes the verification flow across components with:
   * 1. Check the last stored OTP in localStorage and verify its validity
   * 2. If no valid OTP exists, trigger the appropriate action (modal or redirect)
   * 3. Use multiple redirection mechanisms for reliability
   * 
   * @param config Configuration object for the verification
   * @param config.username Optional username to check user-specific last OTP code
   * @param config.from Required source of verification (for tracking and redirection)
   * @param config.onModalSuccess Callback for when modal verification succeeds (only used with legacy approach)
   * @param config.navigate React router navigation function (required if using redirect approach)
   * @param config.useGlobalContext If true, uses the global OTP context instead of component-specific
   * @returns Promise resolving to true if OTP is valid, false if invalid or verification process started
   */
  static async globalVerifySmartOTP(config: {
    username: string;
    from: string; // This is required to track the origin and for redirection
    onInvalidOtp?: Function;
    // Legacy support
    showModal?: boolean;
    setModalVisible?: (visible: boolean) => void; 
    onModalSuccess?: () => void;
    onModalError?: () => void;
    // Navigation
    navigate?: (path: string, options?: any) => void;
    // New global approach
    useGlobalContext?: boolean;
  }): Promise<boolean> {
    const { 
      username, 
      from, 
      onInvalidOtp, 
      setModalVisible,
      onModalSuccess,
      onModalError,
      useGlobalContext = true // Mặc định sử dụng global context
    } = config;
    
    console.log(`Global SmartOTP verification from ${from}`);
    
    // Function to handle invalid OTP cases
    const handleInvalidOtp = () => {
      if (onInvalidOtp) {
        // Custom handling if provided
        onInvalidOtp();
        return;
      }
      
      // Use global context if available and requested
      if (useGlobalContext && globalOtpContextHandler) {
        console.log('Using global OTP context for verification');
        globalOtpContextHandler.setFromScreen(from);
        
        // Set callback for success (if provided)
        if (onModalSuccess) {
          globalOtpContextHandler.setOtpSuccessCallback(onModalSuccess);
        }
        
        // Set callback for error (if provided)
        // Ưu tiên sử dụng onModalError nếu có, nếu không thì dùng onInvalidOtp
        if (onModalError) {
          globalOtpContextHandler.setOtpErrorCallback(() => {
            console.log('OTP error callback triggered from', from);
            onModalError();
          });
        } else if (onInvalidOtp) {
          globalOtpContextHandler.setOtpErrorCallback(() => {
            console.log('OTP error callback triggered from', from);
            // Execute the error callback
            (onInvalidOtp as () => void)();
          });
        }
        
        // Show global OTP modal
        globalOtpContextHandler.showOtpModal();
        return;
      }
      
      // Show modal approach
      if (!setModalVisible) {
        console.error('SmartOTP Modal configuration missing required props');
        return;
      }
      
      console.log('Showing SmartOTP modal for verification (legacy)');
      setModalVisible(true);
    };
    
    // Check if OTP is already verified
    try {
      // Get the last OTP code
      const lastSmartOTP = this.getLastSmartOtpCode(username);
      
      if (!lastSmartOTP) {
        console.log('No last SmartOTP found, starting verification process');
        handleInvalidOtp();
        return false;
      }
      
      // Verify the last OTP code
      const response = await this.verifySmartOTP(lastSmartOTP);
      
      if (response && response.Status === 'Success') {
        console.log('Last SmartOTP is valid, proceeding with operation');
        return true;
      } else {
        console.log('Last SmartOTP verification failed:', response?.Message);
        handleInvalidOtp();
        return false;
      }
    } catch (error) {
      console.error('Error in global SmartOTP verification:', error);
      handleInvalidOtp();
      return false;
    }
  }

  /**
   * Clear OTP related data for a specific user from storage
   * @param username The username to clear data for
   */
  static clearOtpData(username?: string): void {
    const normalizedUsername = this.normalizeUsername(username);
    if (!normalizedUsername) {
      return;
    }
    
    // Clear only the specified user's data
    const secretsJson = localStorage.getItem(StorageKeys.OTP_SECRETS);
    if (!secretsJson) return;
    
    try {
      const secrets = JSON.parse(secretsJson) as OtpSecrets;

      for (const key of this.getUsernameCandidates(normalizedUsername)) {
        if (secrets[key]) {
          delete secrets[key];
        }
      }

      if (Object.keys(secrets).length > 0) {
        localStorage.setItem(StorageKeys.OTP_SECRETS, JSON.stringify(secrets));
      } else {
        localStorage.removeItem(StorageKeys.OTP_SECRETS);
      }
    } catch (error) {
      console.error(`Failed to clear OTP data for user ${normalizedUsername}:`, error);
    }
  }
  
  /**
   * Generate current OTP code
   * @param username The username to generate OTP for
   * @param pin User's PIN
   * @returns Generated OTP code or null if error
   */
  static async generateOtp(username: string, pin: string): Promise<string | null> {
    try {
      // Get stored token
      const tokenData = await this.getOtpToken(username, pin);
      if (!tokenData) return null;
      
      // Parse the token data
      const [sharedToken, storedClientToken] = tokenData.split('.');
      if (!sharedToken || !storedClientToken) return null;

      // Use provided time or get server time or local time
      let time = moment();
      try {
        // Try to get server time for more accuracy
        const serverTime = await fetchApi('get', 'api/SmartOTP/GetServerTime', {}, 'json', { skipCache: true });
        if (serverTime && serverTime.ServerTime) {
          time = moment(serverTime.ServerTime);
        }
      } catch (error) {
        console.log('Error getting server time, using local time:', error);
      }
      
      // Generate new client token with current time
      const currentClientToken = getStableHash(`${pin}.${time.format('yyyyMMDDHHmm')}`)
      
      // Combine tokens to generate OTP
      const combinedToken = `${sharedToken}.${currentClientToken}`;
      const otpHash = this.createSimpleHash(combinedToken);
      
      // Take first 6 digits
      return otpHash.substring(0, 6);
    } catch (error) {
      console.error(`Failed to generate OTP for user ${username}:`, error);
      return null;
    }
  }

  /**
   * Create a hash of a string using SHA256
   * @param str String to hash
   * @returns Hashed string
   */
  private static createSimpleHash(str: string): string {
    return CryptoJS.SHA256(str).toString();
  }
}
